<!-- Корневой компонент приложения -->
<template>
  <div id="app">
    <router-view />
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'

// Инициализация приложения
onMounted(() => {
  // Логи скролла для отладки
  console.log('🔍 [APP] Initial scroll position:', window.scrollY)
  console.log('🔍 [APP] URL hash:', window.location.hash)
  
  // Сброс скролла на начало
  window.scrollTo(0, 0)
  console.log('✅ [APP] Scroll reset to top')
  
  // Отслеживание изменений скролла
  let scrollTimeout: ReturnType<typeof setTimeout>
  window.addEventListener('scroll', () => {
    clearTimeout(scrollTimeout)
    scrollTimeout = setTimeout(() => {
      console.log('📜 [SCROLL] Current position:', window.scrollY)
    }, 100)
  })
  
  // Отслеживание попыток скролла к якорям
  window.addEventListener('hashchange', (e) => {
    console.log('🔗 [HASH CHANGE] From:', e.oldURL, 'To:', e.newURL)
  })
  // Применяем CSS переменные дизайн-токенов
  const style = document.createElement('style')
  style.textContent = `
    :root {
      /* Цвета */
      --color-primary: #ff3f1a;
      --color-primary-foreground: #ffffff;
      --color-secondary: #cefaff;
      --color-accent: #ff6d51;
      --color-muted: rgba(236, 236, 236, 0.7);
      
      --color-success: #10b981;
      --color-warning: #f59e0b;
      --color-danger: #ef4444;
      
      --color-background: #161616;
      --color-surface: rgba(0, 0, 0, 0.1);
      --color-border: rgba(255, 255, 255, 0.25);
      --color-ring: rgba(255, 63, 26, 0.22);
      
      --color-foreground: #ececec;
      --color-foreground-muted: rgba(236, 236, 236, 0.7);
      
      /* Типографика */
      --font-gotham: 'Gotham', system-ui, -apple-system, sans-serif;
      --font-halvar: 'Halvar Breitschrift', system-ui, -apple-system, sans-serif;
      --font-mono: 'JetBrains Mono', 'Courier New', monospace;
      
      --text-xs: 0.75rem;
      --text-sm: 0.875rem;
      --text-base: 1rem;
      --text-lg: 1.125rem;
      --text-xl: 1.25rem;
      --text-2xl: 1.5rem;
      --text-3xl: 1.875rem;
      --text-4xl: 2.25rem;
      
      /* Отступы */
      --spacing-0-5: 0.125rem;
      --spacing-1: 0.25rem;
      --spacing-1-5: 0.375rem;
      --spacing-2: 0.5rem;
      --spacing-3: 0.75rem;
      --spacing-4: 1rem;
      --spacing-6: 1.5rem;
      --spacing-8: 2rem;
      --spacing-12: 3rem;
      --spacing-16: 4rem;
      
      /* Радиусы */
      --radius-sm: 0.25rem;
      --radius-md: 0.5rem;
      --radius-lg: 0.75rem;
      --radius-xl: 1rem;
      --radius-2xl: 1.5rem;
      
      /* Тени */
      --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
      --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
      --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
      --shadow-inner: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06);
    }
  `
  document.head.appendChild(style)
})
</script>

<style>
/* Глобальные стили */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  scroll-behavior: auto;
  /* Убираем smooth для предотвращения автоскролла */
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

body {
  font-family: var(--font-gotham), system-ui, -apple-system, sans-serif;
  background-color: var(--color-background);
  color: var(--color-foreground);
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  overflow-x: hidden;
  overflow-y: visible;
  position: relative;
  min-height: 100%;
}

#app {
  min-height: 100vh;
  position: relative;
}

/* Утилитарные классы для шрифтов */
.font-heading {
  font-family: var(--font-halvar), var(--font-gotham), sans-serif;
}

.font-primary {
  font-family: var(--font-gotham), sans-serif;
}

.font-mono {
  font-family: var(--font-mono), monospace;
}

/* Плавные переходы для всех интерактивных элементов */
a, button, [role="button"] {
  transition: all 0.2s ease-in-out;
}

/* Фокус для доступности */
*:focus {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

/* Скрытие скроллбара для webkit браузеров */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: var(--color-background);
}

::-webkit-scrollbar-thumb {
  background: var(--color-border);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--color-primary);
}

/* Стили для изображений */
img {
  max-width: 100%;
  height: auto;
}

/* Стили для кнопок */
button {
  cursor: pointer;
  border: none;
  background: none;
  font-family: inherit;
}

/* Стили для ссылок */
a {
  color: inherit;
  text-decoration: none;
}

/* Анимации */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInFromLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInFromRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.fade-in {
  animation: fadeIn 0.6s ease-out;
}

.slide-in-left {
  animation: slideInFromLeft 0.6s ease-out;
}

.slide-in-right {
  animation: slideInFromRight 0.6s ease-out;
}

.text-gradient {
  background: linear-gradient(90deg, #ff3f1a 0%, #ff6d51 50%, #ff3f1a 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Responsive утилиты */
@media (max-width: 640px) {
  .container {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

@media (max-width: 768px) {
  .text-responsive {
    font-size: 0.875rem;
  }
}
</style>








